import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="notification-dropdown"
export default class extends Controller {
  static targets = ["button", "menu", "template"];

  connect() {
    // Clean up any existing dropdown popups when the page loads
    this.clearAllDropdowns();

    // Add event listener for clicks outside the dropdown
    document.addEventListener("click", this.handleOutsideClick.bind(this));
  }

  disconnect() {
    // Remove scroll listeners and dropdowns when controller disconnects
    window.removeEventListener("scroll", this.scrollHandler);
    document.removeEventListener("click", this.handleOutsideClick.bind(this));
    this.clearAllDropdowns();
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    const buttonId = event.currentTarget.id.split("-").pop();

    // Check if dropdown is already open
    const isExpanded =
      event.currentTarget.getAttribute("aria-expanded") === "true";

    // Close all dropdowns
    this.clearAllDropdowns();

    // If it was already open, we're done (toggle off)
    if (isExpanded) {
      return;
    }

    // Create new dropdown element
    const templateId = `dropdown-content-${buttonId}`;
    const template = document.getElementById(templateId);

    if (!template) return;

    // Clone the template content
    const content = template.content.cloneNode(true);

    // Create a container for the dropdown
    const dropdownMenu = document.createElement("div");
    dropdownMenu.className = "notification-dropdown-menu";
    dropdownMenu.setAttribute("role", "menu");
    dropdownMenu.setAttribute("aria-labelledby", event.currentTarget.id);
    dropdownMenu.setAttribute("data-button-id", buttonId);

    // Style the dropdown
    dropdownMenu.style.position = "absolute";
    dropdownMenu.style.zIndex = "99999";
    dropdownMenu.style.boxShadow =
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)";
    dropdownMenu.style.border = "1px solid rgba(0, 0, 0, 0.1)";
    dropdownMenu.style.backgroundColor = "#1e293b";
    dropdownMenu.style.borderRadius = "0.375rem";

    // Add content to dropdown
    dropdownMenu.appendChild(content);

    // Append to body
    document.body.appendChild(dropdownMenu);

    // Position the dropdown
    this.positionDropdown(dropdownMenu, event.currentTarget);

    // Add scroll event listener to update position
    this.scrollHandler = this.handleScroll.bind(this, buttonId, dropdownMenu);
    window.addEventListener("scroll", this.scrollHandler);

    // Mark button as expanded
    event.currentTarget.setAttribute("aria-expanded", "true");
  }

  // Clear all open dropdowns
  clearAllDropdowns() {
    document
      .querySelectorAll(".notification-dropdown-menu")
      .forEach(function (el) {
        el.remove();
      });

    // Reset all buttons
    document
      .querySelectorAll('[id^="dropdown-button-"]')
      .forEach(function (button) {
        button.setAttribute("aria-expanded", "false");
      });
  }

  // Handle outside clicks to close dropdowns
  handleOutsideClick(event) {
    if (
      !event.target.closest('[id^="dropdown-button-"]') &&
      !event.target.closest(".notification-dropdown-menu")
    ) {
      this.clearAllDropdowns();
    }
  }

  // Handle form submit completion
  handleSubmit(event) {
    // Close the dropdown after successful submission
    this.clearAllDropdowns();
  }

  // Handle scroll updates
  handleScroll(buttonId, dropdownMenu) {
    const button = document.getElementById(`dropdown-button-${buttonId}`);
    if (
      button &&
      document.querySelector(
        `.notification-dropdown-menu[data-button-id="${buttonId}"]`
      )
    ) {
      this.positionDropdown(dropdownMenu, button);
    } else {
      // Button or dropdown no longer exists, remove scroll listener
      window.removeEventListener("scroll", this.scrollHandler);
    }
  }

  // Position dropdown correctly
  positionDropdown(dropdown, button) {
    if (!button || !dropdown) return;

    const buttonRect = button.getBoundingClientRect();

    // Position dropdown at the exact coordinates of the button + scroll position
    dropdown.style.width = Math.max(280, buttonRect.width) + "px"; // Even wider minimum width
    dropdown.style.top = window.scrollY + buttonRect.bottom + 5 + "px";
    dropdown.style.left = window.scrollX + buttonRect.left + "px";

    // Check if dropdown is visible within viewport, if not, position it above the button
    const dropdownHeight = dropdown.offsetHeight;
    const viewportHeight = window.innerHeight;
    const bottomSpace = viewportHeight - buttonRect.bottom;

    if (dropdownHeight > bottomSpace && buttonRect.top > dropdownHeight) {
      // Position above button if there's more space above
      dropdown.style.top =
        window.scrollY + buttonRect.top - dropdownHeight - 5 + "px";
    }
  }
}
